import { Amount } from '@ui/components/Amount/Amount';
import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { getPayablePoolTokenBySymbol } from '@ui/constants/tokens';
import { PoolTokenSymbol } from '@ui/constants/tokens/tokenSymbols';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { useDataChainId } from '@ui/hooks/datachain';
import { usePriceLatest } from '@ui/hooks/price/usePriceLatest';
import { useErc20Symbol } from '@ui/hooks/useErc20Read';
import { usePayTokenBalance } from '@ui/hooks/usePayTokenBalance';
import { Text } from '@ui/lib/park/ui/text';
import { ERC20Token, Token } from '@ui/types/token';
import { useTranslation } from 'next-i18next';
import { Box, Flex, HStack, styled } from 'styled-system/jsx';

import { SupportedSymbol } from '../../../hooks/price/priceLatest/store';
import { useAddTokenToWallet } from '../hooks/useAddTokenToWallet';

export const BalanceSection = ({ tokens }: { tokens: Token[] }) => {
  const { t } = useTranslation();

  return (
    <Flex direction="column" gap="0px" mt="4px">
      <Box
        px="14px"
        pt="10px"
        pb="4px"
        borderTop="1px solid"
        borderColor="v2.border.hairline"
      >
        <Text textStyle="v2.sectionLabel" color="v2.text.subtle">
          {t('common.Balances')}
        </Text>
      </Box>

      <Box pb="8px">
        {tokens.map((token) => (
          <TokenBalanceRow key={token.symbol} token={token} />
        ))}
      </Box>
    </Flex>
  );
};

const TokenBalanceRow = ({ token }: { token: Token }) => {
  const chainId = useDataChainId();
  const balance = usePayTokenBalance(token);
  const poolToken = getPayablePoolTokenBySymbol(
    token.symbol as PoolTokenSymbol,
    chainId,
  );
  const payTokenPrice = usePayTokenPriceLatest(poolToken);
  const defaultPrice = usePriceLatest(token.symbol as SupportedSymbol);
  const price = payTokenPrice ?? defaultPrice;

  const isErc20 = !token.isNative && !!token.address;
  const tokenAddress = isErc20 ? (token.address as ERC20Token['address']) : null;

  const { data: contractSymbol } = useErc20Symbol({
    tokenAddress,
  });
  const { watchAsset, isPending } = useAddTokenToWallet({
    tokenDisplaySymbol: token.displaySymbol,
  });

  return (
    <HStack
      gap="5"
      w="full"
      justifyContent="space-between"
      alignItems="center"
      cursor="default"
      px="14px"
      py="6px"
      minH="34px"
    >
      <styled.button
        type="button"
        disabled={isPending}
        title={isErc20 ? 'add token to wallet' : undefined}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();

          if (!isErc20) return;

          const image =
            typeof window !== 'undefined'
              ? new URL(token.image, window.location.origin).toString()
              : token.image;

          watchAsset({
            type: 'ERC20',
            options: {
              address: tokenAddress ?? '',
              symbol: (contractSymbol ?? token.displaySymbol ?? '').toString(),
              decimals: token.decimals ?? 0,
              image,
            },
          });
        }}
        display="inline-flex"
        alignItems="center"
        gap="9px"
        bg="transparent"
        border="none"
        p={0}
        m={0}
        textAlign="left"
        cursor={isErc20 ? 'pointer' : 'default'}
        aria-label={`Add ${token.displaySymbol} to wallet`}
        data-cy={`WalletBalanceTokenAdd-${token.displaySymbol}`}
      >
        <styled.img
          src={token.image}
          alt="token icon"
          width="20px"
          height="20px"
        />
        <Text textStyle="v2.label" color="v2.text.primary">
          {token.displaySymbol}
        </Text>
      </styled.button>
      <Flex
        flexDir="column"
        justifyContent="center"
        textAlign="right"
        gap="0px"
      >
        {balance && (
          <Amount
            textStyle="numbers.small"
            color="v2.text.primary"
            value={balance}
          />
        )}

        {price && balance && (
          <AmountUsd
            textStyle="numbers.micro"
            color="v2.text.subtle"
            value={price.times(balance)}
            opts={{
              prefix: '≈ $',
            }}
          />
        )}
      </Flex>
    </HStack>
  );
};
